import {
  getReviewOptions,
  listAllEmbodiments,
  listAllLabels,
  listAllTips,
  listDatasetCameraChannels,
  listLabelCategories,
  type LabelCategory,
} from "@/api";
import { splitTagSelection } from "@/components/label";
import type { FilterGroup, FilterSelection } from "@carbon6/c6-frontend-design";
import {
  createCameraChannelFacetGroup,
  createCameraCountFacetGroup,
  createDatasetLabelFilterGroups,
  createEmbodimentFacetGroups,
  createEpisodeQualityFacetGroup,
  createLabelColorIndex,
  createLabelTokenOptions,
  createStaticFacetGroups,
  createTipFacetGroups,
  isDatasetFacetParamKey,
  selectCuratedFacetGroups,
  type DatasetFacetParamKey,
  type LabelConditionOption,
} from "./datasetFilters";

// 일치 방식이다. 선택한 필터 조건(facet 값·generic 라벨)을 모두 만족(AND)할지, 하나라도 만족(OR)할지
// 정한다. 기본은 AND(all)다. backend 에는 filter_match 설정 하나(값 사이 + 축 사이)와
// labels_all/labels_any 로 변환해 보낸다.
export type DatasetMatchMode = "any" | "all";

// labels_all/any 와 전용 facet 파라미터(+ filter_match)를 담는 필터 query 조각이다.

export type LabelFilterQuery = Partial<Record<DatasetFacetParamKey, string>> & {
  // 에피소드 결함 갈래 code 다. `episode_quality` 선택 중 갈래 행(`category:<code>`)만 분리해 담는다.
  episode_quality_category?: string;
  labels_all?: string;
  labels_any?: string;
  filter_match?: DatasetMatchMode;
};

// c6 Filter 선택을 백엔드 query 로 변환한다.
// - 전용 facet(embodiment / camera_channel / environment …)은 key 별 파라미터 하나(콤마 IN)로 보낸다.
// - generic 라벨(라벨 토큰 검색 선택)은 mode 에 따라 labels_all(AND)/labels_any(OR)로 묶는다.
// mode 기본값 AND(all)는 backend filter_match 기본값과 같다. 그래서 any(OR)이고 조건이 있을 때만
// filter_match=any 를 보낸다. 이 설정 하나로 값 사이와 축 사이가 함께 OR 가 된다.
// 에피소드 결함·행동 출처 축 안의 값은 mode와 무관하게 OR다.
// 검색어·status·reviewed·duration 은 backend 가 항상 AND 조건으로 적용한다.
export function createLabelFilterQuery(
  selection: FilterSelection,
  mode: DatasetMatchMode = "all",
): LabelFilterQuery {
  const facetQuery: Pick<LabelFilterQuery, DatasetFacetParamKey | "episode_quality_category"> = {};
  const genericLabels: string[] = [];

  for (const [key, values] of Object.entries(selection)) {
    const activeValues = values.map((value) => value.trim()).filter((value) => value !== "");

    if (activeValues.length === 0) {
      continue;
    }

    // 에피소드 결함은 한 드롭다운에 값·토큰·갈래가 섞여 있다. 서버는 갈래만 다른 파라미터로
    // 받고, 둘을 한 축 OR 로 합친다.
    if (key === "episode_quality") {
      const { tags, categories } = splitTagSelection(activeValues);
      if (tags.length > 0) {
        facetQuery.episode_quality = tags.join(",");
      }
      if (categories.length > 0) {
        facetQuery.episode_quality_category = categories.join(",");
      }
      continue;
    }

    if (isDatasetFacetParamKey(key)) {
      facetQuery[key] = activeValues.join(",");
      continue;
    }

    genericLabels.push(...activeValues.map((value) => key + ":" + value));
  }

  const genericQuery: Pick<LabelFilterQuery, "labels_all" | "labels_any"> =
    genericLabels.length === 0
      ? {}
      : mode === "all"
        ? { labels_all: genericLabels.join(",") }
        : { labels_any: genericLabels.join(",") };
  const hasCondition = Object.keys(facetQuery).length > 0 || genericLabels.length > 0;

  return {
    ...facetQuery,
    ...genericQuery,
    // backend 기본값(filter_match=all)과 다른 any 이고, 조건이 있을 때만 보낸다.
    ...(mode === "any" && hasCondition ? { filter_match: "any" } : {}),
  };
}

// 빈 값만 가진 그룹을 버려 selection 을 정규화한다. 그래서 비교와 query 생성 결과가 안정적이다.
export function normalizeFilterSelection(selection: FilterSelection): FilterSelection {
  const nextSelection: Record<string, readonly string[]> = {};
  for (const [groupValue, filterValues] of Object.entries(selection)) {
    if (filterValues.length > 0) {
      nextSelection[groupValue] = Array.from(filterValues);
    }
  }
  return nextSelection;
}

export function hasActiveFilterSelection(selection: FilterSelection): boolean {
  return Object.values(selection).some((filterValues) => filterValues.length > 0);
}

// 활성 필터 조건(공백 제거 후 남는 값)의 수다. createLabelFilterQuery 와 같은 공백 규약을 쓴다.
// 조합 방식(AND/OR)에 따라 결과가 실제로 달라지는 최소 조건은 "predicate 2개 이상"이다. 그래서
// 레일의 조합 방식 배지 표시 판정(라벨 토큰 수와 합산)이 이 수를 쓴다.
export function countFilterPredicates(selection: FilterSelection): number {
  return Object.values(selection).reduce(
    (count, filterValues) => count + filterValues.filter((value) => value.trim() !== "").length,
    0,
  );
}

export function isFilterSelectionEqual(left: FilterSelection, right: FilterSelection): boolean {
  const leftEntries = Object.entries(left);
  const rightEntries = Object.entries(right);
  if (leftEntries.length !== rightEntries.length) {
    return false;
  }

  return leftEntries.every(([groupValue, leftValues]) => {
    const rightValues = right[groupValue] ?? [];
    return (
      leftValues.length === rightValues.length &&
      leftValues.every((value, index) => value === rightValues[index])
    );
  });
}

export type DatasetLabelFilterCatalog = {
  // 필터 바에 표시할 facet 그룹이다(CURATED_FACETS 순서).
  groups: readonly FilterGroup[];
  // 라벨 조건 후보다(generic 라벨 key:value).
  labelTokenOptions: readonly LabelConditionOption[];
  // 모든 카탈로그 라벨의 색이다(`key:value` → 0xRRGGBB). 추천 후보 행의 칩이 쓴다.
  labelColors: ReadonlyMap<string, number>;
};

// 라벨·embodiment·카메라 채널·tip 카탈로그를 받아 필터 facet 그룹과 라벨 토큰 옵션을 만든다. 빌더의
// 그룹을 union 한 뒤 selectCuratedFacetGroups 로 표시 대상만 골라 순서를 정한다(순서·라벨의 SoT 는
// CURATED_FACETS 다). 옵션의 출처:
// - camera_channel: 데이터셋 실측 채널 인벤토리(distinct-channel 엔드포인트)
// - camera_count: 하드코딩 프리셋
// - 에피소드 결함: review-options 의 에피소드 축 어휘(세션 캐시). 실패하면 빈 배열이라 그 축만 표시하지 않는다.
// - 갈래 행과 소속: label-categories(세션 캐시). 실패하면 갈래 없이 값만 표시한다.
// Dataset·북마크 목록·Review·팀 학습이 같은 카탈로그를 함께 쓴다. 선택지별 수는 여기가 아니라
// facets 가 폴더 단위로 추가한다.
export async function loadDatasetLabelFilterCatalog(): Promise<DatasetLabelFilterCatalog> {
  const [labels, embodiments, cameraChannels, tips, reviewOptions, qualityCategories] =
    await Promise.all([
      listAllLabels(),
      listAllEmbodiments(),
      listDatasetCameraChannels(),
      listAllTips(),
      getReviewOptions(),
      listLabelCategories("quality").catch((): readonly LabelCategory[] | null => null),
    ]);
  return {
    groups: selectCuratedFacetGroups([
      ...createEmbodimentFacetGroups(embodiments),
      ...createCameraChannelFacetGroup(cameraChannels),
      ...createCameraCountFacetGroup(),
      ...createDatasetLabelFilterGroups(labels),
      ...createTipFacetGroups(tips),
      ...createStaticFacetGroups(),
      ...createEpisodeQualityFacetGroup(reviewOptions.episode_quality_tags, qualityCategories),
    ]),
    labelTokenOptions: createLabelTokenOptions(labels),
    labelColors: createLabelColorIndex(labels),
  };
}
